<template>
  <div class="container mt-5">
    <h2 class="text-center mb-4">Heti teljes fogyás</h2>

    <!-- Show loading message or spinner while waiting for the data -->
    <div v-if="loading" class="text-center">
      <p>Adatok betöltése...</p> <!-- You can also add a spinner here if preferred -->
    </div>

    <!-- Filter Section -->
    <div v-if="!loading" class="mb-4">
      <h4>Szűrő beállítások</h4>
      
      <!-- Month filter -->
      <label for="monthFilter">Hónap:</label>
      <select id="monthFilter" v-model="selectedMonth" class="form-control mb-3" @change="onMonthChange">
        <option value="">Mind</option>
        <option v-for="n in 12" :key="n" :value="n">{{ monthEnum[n-1] }}</option>
      </select>

      <!-- From-To Date filter -->
      <div class="form-inline">
        <label for="fromDate" class="mr-2">Dátum tartomány (from):</label>
        <input type="date" v-model="fromDate" id="fromDate" class="form-control mr-2" @change="onDateRangeChange">

        <label for="toDate" class="mr-2">Dátum tartomány (to):</label>
        <input type="date" v-model="toDate" id="toDate" class="form-control" @change="onDateRangeChange">
      </div>
    </div>

    <!-- Download Button -->
    <div v-if="!loading" class="mb-4">
      <button @click="downloadXLSX" class="btn btn-primary">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7,10 12,15 17,10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        XLSX Letöltés
      </button>
    </div>

    <!-- Table to display the data, only shows after data is loaded -->
    <table class="table table-bordered table-striped">
      <thead class="thead-dark">
        <tr>
          <th>Hét</th>
          <th>Időszak</th>
          <th>Összesítve</th>
        </tr>
      </thead>
      <tbody>
        <!-- Loop through the filtered data to create table rows -->
        <tr v-for="entry in filteredData" :key="entry.count_id">
          <td>{{ entry.count_id }}. hét</td>
          <td>{{ getWeekDateRange(entry.count_id) }}</td>
          <td>{{ entry.total_attended_count }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
// Import axios
import axios from 'axios';

export default {
  name: 'AttendanceTable',
  data() {
    return {
      attendanceData: [], // Data to be populated from the backend
      filteredData: [],    // Filtered data based on selected filters
      loading: true,       // Loading state to control when to show the table or the loading message
      currentYear: new Date().getFullYear(), // Dynamically get the current year
      selectedMonth: '',   // Selected month filter
      fromDate: '',        // From date filter
      toDate: '',          // To date filter
      monthEnum : [
        'Január',
        'Február',
        'Március',
        'Április',
        'Május',
        'Június',
        'Július',
        'Augusztus',
        'Szeptember',
        'Oktober',
        'November',
        'December'
      ]
    };
  },
  methods: {
    // Method to fetch data from the backend
    fetchAttendanceData() {
      axios.post(this.$BACKEND_HOST + '/api/getstatistics/allweek', { year: this.currentYear, grp_id: -1 })
        .then(response => {
          this.attendanceData = response.data.value;
          this.filteredData = [...this.attendanceData];  // Initialize filteredData with all fetched data
          console.log(this.filteredData);
        })
        .catch(error => {
          console.error('There was an error fetching the attendance data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Method to calculate the start and end dates of a given week
    getWeekDateRange(weekNumber) {
      const startOfYear = new Date(this.currentYear, 0, 1);
      const dayOfWeek = startOfYear.getDay();
      const daysToAdd = (weekNumber - 1) * 7 - (dayOfWeek === 0 ? 6 : dayOfWeek - 1);

      const startDate = new Date(startOfYear);
      startDate.setDate(startDate.getDate() + daysToAdd);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      const startDateFormatted = this.formatDate(startDate);
      const endDateFormatted = this.formatDate(endDate);

      return `${startDateFormatted} - ${endDateFormatted}`;
    },

    formatDate(date) {
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('hu-HU'); // Format date as "YYYY-MM-DD"
    },

    // Method to apply filters based on month and date range
    applyFilters() {
      this.filteredData = this.attendanceData.filter(entry => {
        const weekStartDate = this.getWeekStartDate(entry.count_id);

        // Filter by month if selected
        const monthMatches = this.selectedMonth ? (weekStartDate.getMonth() + 1 === this.selectedMonth) : true;

        // Filter by date range (from date and to date)
        const fromDateMatches = this.fromDate ? (weekStartDate >= new Date(this.fromDate)) : true;
        const toDateMatches = this.toDate ? (weekStartDate <= new Date(this.toDate)) : true;

        return monthMatches && fromDateMatches && toDateMatches;
      });
    },

    // Get the start date of the given week number
    getWeekStartDate(weekNumber) {
      const startOfYear = new Date(this.currentYear, 0, 1);
      const dayOfWeek = startOfYear.getDay();
      const daysToAdd = (weekNumber - 1) * 7 - (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
      const startDate = new Date(startOfYear);
      startDate.setDate(startDate.getDate() + daysToAdd);
      return startDate;
    },

    // Reset date range when the month changes
    onMonthChange() {
      this.fromDate = '';
      this.toDate = '';
      this.applyFilters();
    },

    // Reset month when the date range changes
    onDateRangeChange() {
      this.selectedMonth = '';
      this.applyFilters();
    },

    // Method to download data as XLSX
    downloadXLSX() {
      const params = new URLSearchParams();
      
      // Add year parameter
      params.append('year', this.currentYear);
      
      // Add filter parameters if they are set
      if (this.selectedMonth) {
        params.append('month', this.selectedMonth);
      }
      if (this.fromDate) {
        params.append('from_date', this.fromDate);
      }
      if (this.toDate) {
        params.append('to_date', this.toDate);
      }

      // Create download link
      const link = document.createElement('a');
      link.href = `${this.$BACKEND_HOST}/api/downloadstatistics/allweek?${params.toString()}`;
      link.download = 'heti_teljes_fogyas.xlsx';
      link.style.display = 'none';

      // Append to body and trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
    },
  },
  mounted() {
    this.fetchAttendanceData();
  }
};
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn:hover {
  background: linear-gradient(135deg, #0056b3, #004085);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0056b3, #004085);
}
</style>
